import apiClient from '../core';
import type {
  LoginRequest,
  AuthResponse,
  SignupRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest,
} from '../../schemas/validation';

const verifyEmailInFlight = (() => {
  const map = new Map<string, Promise<AuthResponse>>();
  return {
    getOrCreate(token: string, fn: () => Promise<AuthResponse>): Promise<AuthResponse> {
      const existing = map.get(token);
      if (existing) return existing;
      const p = fn().finally(() => {
        map.delete(token);
      });
      map.set(token, p);
      return p;
    },
  };
})();

export const authApi = {
  async login(data: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/login', data);

    // Store the token
    localStorage.setItem('authToken', response.data.token);

    return response.data;
  },

  async signup(data: SignupRequest): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/signup', data);

    // Store the token
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }

    return response.data;
  },

  logout(): void {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
  },

  async forgotPassword(data: ForgotPasswordRequest): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/forgot-password', data);
    return response.data;
  },

  async resetPassword(data: ResetPasswordRequest): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/reset-password', data);
    return response.data;
  },

  async verifyPassword(password: string): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post<{
      success: boolean;
      message: string;
    }>('/auth/verify-password', { password });
    return response.data;
  },

  async verifyEmailWithToken(token: string): Promise<AuthResponse> {
    // Dedupe: React 18 Strict Mode double-invokes effects; a second POST could burn the one-time token.
    return verifyEmailInFlight.getOrCreate(token, async () => {
      const response = await apiClient.post<AuthResponse>('/auth/verify-email', { token });
      if (response.data.token) {
        localStorage.setItem('authToken', response.data.token);
      }
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
      return response.data;
    });
  },

  async resendVerification(): Promise<{ message: string }> {
    const response = await apiClient.post<{ message: string }>('/auth/resend-verification', {});
    return response.data;
  },

  /** Exchange a Studio → Pages one-time handoff code for a session. */
  async exchangeStudioHandoff(code: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>(
      '/auth/studio-handoff/exchange',
      { code }
    );
    if (response.data.token) {
      localStorage.setItem('authToken', response.data.token);
    }
    if (response.data.user) {
      localStorage.setItem('user', JSON.stringify(response.data.user));
    }
    return response.data;
  },
};
